import React, { useState, useEffect } from "react";
import styles from "./styles.module.css";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { useTranslation } from "react-i18next";
import ShopService from "services/ShopService";
import DateTimeHelpers from "helpers/DateTimeHelpers";

const STATUS = {
  REQUESTED: "requested",
  CONFIRMATION: "confirmation",
};

function ShopDeleteConfirmationPopup({ handleClose, shop }) {
  const { t: tCommon } = useTranslation("common");
  const { t: tPopup } = useTranslation("popup");

  const [isLoading, setLoading] = useState(false);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const [popupStep, setPopupStep] = useState(STATUS.CONFIRMATION);

  const scheduledDate =
    deleteRequest?.delete_scheduled_at
      ? DateTimeHelpers.getDate(new Date(deleteRequest.delete_scheduled_at))
      : "";

  useEffect(() => {
    async function fetchStatus() {
      try {
        const res = await ShopService.getDeletionStatus();
        if (res?.delete_scheduled_at) {
          setDeleteRequest(res);
          setPopupStep(STATUS.REQUESTED);
        } else {
          setDeleteRequest(null);
          setPopupStep(STATUS.CONFIRMATION);
        }
      } catch (e) {
        console.error("Failed to fetch deletion status", e);
      }
    }
    fetchStatus();
  }, []);

  const handleDeleteShop = async () => {
    setLoading(true);
    try {
      const res = await ShopService.requestShopDeletion(shop.id);
      setDeleteRequest(res);
      setPopupStep(STATUS.REQUESTED);
    } catch (err) {
      console.error(err);
      alert("Failed to request deletion");
    } finally {
      setLoading(false);
    }
  };

  const onCancelDeletion = async () => {
    setLoading(true);
    try {
      await ShopService.cancelDeletion();
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Failed to cancel deletion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={popupStep === STATUS.CONFIRMATION ? styles.popUp : styles.popupOverlay}>
      <div className={styles.popupModal}>
        <div className={styles.popupHeader}>
          <div className={styles.popupHeaderContent}>
            <div className={`${styles.popupIcon} ${styles.popupIconExpired}`}>
              <WarningRoundedIcon size={24} />
            </div>
            <div>
              <h2 className={styles.popupTitle}>
                {popupStep === STATUS.CONFIRMATION
                  ? tPopup("Confirm Shop Deletion")
                  : tPopup("Shop Deletion Scheduled")}
              </h2>
              <p className={styles.popupPlan}>{shop?.shop_name}</p>
            </div>
          </div>
        </div>

        <div className={styles.popupContent}>
          {popupStep === STATUS.CONFIRMATION ? (
            <div className={styles.popupMessage}>
              <p>{tPopup("Are you sure you want to delete your shop '{{shopName}}'? You can reverse this action within the next 30 days.", { shopName: shop?.shop_name })}</p>
            </div>
          ) : (
            <div className={`${styles.popupAlert} ${styles.popupAlertExpired}`}>
              {tPopup("Your shop deletion is scheduled on {{scheduledDeletionDate}}.", { scheduledDeletionDate: scheduledDate })}
              {tPopup("You can cancel your shop deletion anytime till scheduled date.")}
            </div>
          )}

          <div className={styles.popupActions}>
            {popupStep === STATUS.CONFIRMATION ? (
              <>
                <button
                  className={`${styles.popupRenew} ${styles.popupRenewExpired}`}
                  onClick={handleDeleteShop}
                  disabled={isLoading}
                >
                  {isLoading ? tCommon("Loading...") : tCommon("Yes, delete")}
                </button>
                <button className={styles.popupLater} onClick={handleClose} disabled={isLoading}>
                  {tCommon("Cancel")}
                </button>
              </>
            ) : (
              <button
                className={`${styles.popupRenew} ${styles.popupRenewExpired}`}
                onClick={onCancelDeletion}
                disabled={isLoading}
              >
                {isLoading ? tCommon("Loading...") : tCommon("Cancel")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShopDeleteConfirmationPopup;
